export default function BriefingPackets({ missions = [], warrantPlans = [] }) {
  function getLinkedPlan(operation) {
    return warrantPlans.find(
      (p) =>
        p.operationId === operation.id ||
        p.missionId === operation.id
    ) || null;
  }

  function exportPacket(operation) {
    const linkedPlan = getLinkedPlan(operation);

    const esc = (value) =>
      String(value || "").replace(/[&<>"']/g, (m) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;"
      }[m]));

    const win = window.open("", "_blank");
    if (!win) return alert("Popup blocked.");

    win.document.write(`
      <html>
        <head>
          <title>TacOps Operations Packet</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 36px;
              color: #111;
            }

            h1 {
              margin-bottom: 2px;
              letter-spacing: .03em;
            }

            h2 {
              border-bottom: 1px solid #999;
              padding-bottom: 4px;
              margin-top: 24px;
            }

            .sub {
              color: #666;
              margin-bottom: 20px;
            }

            .grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }

            .box {
              border: 1px solid #ccc;
              padding: 10px;
              margin: 8px 0;
              page-break-inside: avoid;
            }

            .label {
              font-size: 11px;
              font-weight: bold;
              color: #555;
              text-transform: uppercase;
            }

            .value {
              margin-top: 4px;
              white-space: pre-wrap;
              line-height: 1.35;
            }

            @media print {
              button {
                display: none;
              }
            }
          </style>
        </head>

        <body>
          <button onclick="window.print()">Print / Save as PDF</button>

          <h1>TacOps Operations Packet</h1>
          <div class="sub">TacOps by Aegis Group</div>

          <h2>Operation Overview</h2>

          <div class="grid">
            <div class="box">
              <div class="label">Operation</div>
              <div class="value">${esc(operation.title)}</div>
            </div>

            <div class="box">
              <div class="label">Operation Type</div>
              <div class="value">${esc(operation.operationType || operation.missionType)}</div>
            </div>

            <div class="box">
              <div class="label">Commander</div>
              <div class="value">${esc(operation.commander)}</div>
            </div>

            <div class="box">
              <div class="label">Team Lead</div>
              <div class="value">${esc(operation.teamLead)}</div>
            </div>

            <div class="box">
              <div class="label">Date / Time</div>
              <div class="value">${esc(operation.operationDate)}</div>
            </div>

            <div class="box">
              <div class="label">Location</div>
              <div class="value">${esc(operation.location)}</div>
            </div>
          </div>

          <div class="box">
            <div class="label">Operation Objective</div>
            <div class="value">${esc(operation.objective)}</div>
          </div>

          ${
            linkedPlan
              ? `
              <h2>Operations Plan</h2>

              <div class="box">
                <div class="label">Threat Assessment</div>
                <div class="value">${esc(linkedPlan.threatAssessment)}</div>
              </div>

              <div class="box">
                <div class="label">Known Weapons</div>
                <div class="value">${esc(linkedPlan.knownWeapons)}</div>
              </div>

              <div class="box">
                <div class="label">Occupants</div>
                <div class="value">${esc(linkedPlan.occupants)}</div>
              </div>

              <div class="box">
                <div class="label">Vehicles</div>
                <div class="value">${esc(linkedPlan.vehicles)}</div>
              </div>

              <div class="box">
                <div class="label">Entry / Action Plan</div>
                <div class="value">${esc(linkedPlan.entryPlan)}</div>
              </div>

              <div class="box">
                <div class="label">Containment Plan</div>
                <div class="value">${esc(linkedPlan.containmentPlan)}</div>
              </div>

              <div class="box">
                <div class="label">Medical Plan</div>
                <div class="value">${esc(linkedPlan.medicalPlan)}</div>
              </div>

              <div class="box">
                <div class="label">Communications Plan</div>
                <div class="value">${esc(linkedPlan.commsPlan)}</div>
              </div>
            `
              : `
              <div class="box">
                <div class="value">No linked Operations Plan found.</div>
              </div>
            `
          }

          <h2>Assignments</h2>

          <div class="box">
            <div class="label">Assigned Operators</div>
            <div class="value">${esc(operation.assignedOperators)}</div>
          </div>

          <div class="box">
            <div class="label">Equipment</div>
            <div class="value">${esc(operation.equipment)}</div>
          </div>

          <div class="box">
            <div class="label">Safety Notes</div>
            <div class="value">${esc(operation.safety)}</div>
          </div>

          <div class="box">
            <div class="label">Staging</div>
            <div class="value">${esc(operation.staging)}</div>
          </div>

          <h2>Timeline</h2>

          ${
            (operation.timeline || []).map((t) => `
              <div class="box">
                <div class="label">${esc(new Date(t.time).toLocaleString())}</div>
                <div class="value"><b>${esc(t.title)}</b><br>${esc(t.detail)}</div>
              </div>
            `).join("")
          }
        </body>
      </html>
    `);

    win.document.close();
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Operations Packets</h2>
          <p className="muted">
            Generate operations packets from operations and attached operations plans.
          </p>
        </div>
      </div>

      <div className="mission-list">
        {missions.map((operation) => {
          const linked = getLinkedPlan(operation);

          return (
            <div className="mission-card" key={operation.id}>
              <div className="mission-main">
                <div className="mission-title">
                  {operation.title}
                </div>

                <div className="mission-meta">
                  {(operation.operationType || operation.missionType || "Operation")} • {operation.location || "No location"}
                </div>

                <p>
                  {linked
                    ? "Operations plan linked and ready for packet generation."
                    : "No operations plan linked yet."}
                </p>
              </div>

              <div className="mission-actions">
                <button
                  className="primary"
                  onClick={() => exportPacket(operation)}
                >
                  Generate Packet
                </button>
              </div>
            </div>
          );
        })}

        {missions.length === 0 ? (
          <div className="empty">No operations available for packet generation.</div>
        ) : null}
      </div>
    </section>
  );
}
